import React, { useContext, useEffect } from 'react';
import { sendScreenEvent } from '@useblu/blu-lytics';

import PaymentLinkContext from '@/pages/Checkout/context/PaymentLinkContext';

import FormBox from '@/components/FormBox';
import * as FormComponents from '@/components/Template/Form';
import Actions from '@/components/Actions';
import InputControl from '@/components/Shared/InputControl';
import TooltipInfo from '@/components/Shared/TooltipInfo';
import InternalPage from '@/components/InternalPage';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { validationCustomerCardSchema } from '@/validations/clientPersonalInfoValidation';
import { IUserCard } from '@/pages/Checkout/types/paymentLinkContextTypes';

import { formatToPattern } from '@/utils/formatToPattern';

const CreditCard: React.FC = () => {
  const {
    setCurrentStep,
    userData,
    setUserData,
    submitCheckout,
    installmentsOptions,
    isMobile,
    maxSteps,
  } = useContext(PaymentLinkContext);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<IUserCard>({
    resolver: yupResolver(validationCustomerCardSchema),
    defaultValues: userData,
  });

  const handleSubmitCardInformation = handleSubmit((userPayload: IUserCard) => {
    const data = { ...userData, ...userPayload };

    setUserData(data);
    submitCheckout(data);
  });

  const handleChangeValue = (
    name:
      | 'card_number'
      | 'card_valid_date'
      | 'card_client_name'
      | 'card_cvv'
      | 'installments',
    value: string,
  ) => {
    setValue(name, value);
    setUserData({ ...userData, [name]: value });
  };

  const formatWithMask = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = /[^\d]/g;

    const cardPatters = {
      15: '0000 000000 00000',
      16: '0000 0000 0000 0000',
      18: '00000 00000 0000 0000',
      19: '00000 00000 00000 0000',
    };

    const { value } = target;

    const cleanValue = value
      .replace(/\s/g, '')
      .replace(onlyDigits, '')
      .slice(0, 19);

    const pattern = cardPatters[cleanValue.length as keyof typeof cardPatters];

    const formated = pattern
      ? formatToPattern(pattern, cleanValue)
      : cleanValue;

    handleChangeValue('card_number', formated);
  };

  useEffect(() => {
    sendScreenEvent('link_blu_checkout_credit_card_view');
  }, []);

  return (
    <InternalPage
      columns={8}
      title="Quais os dados do cartão?"
      steps={{ currentStep: isMobile ? 4 : 3, steps: maxSteps }}
    >
      <FormBox>
        <form onSubmit={handleSubmitCardInformation}>
          <div>
            <FormComponents.FormRow>
              <FormComponents.FormColumn md="6">
                <InputControl
                  label="Número do cartão"
                  name="card_number"
                  placeholder="0000 0000 0000 0000"
                  errors={errors}
                  register={register}
                  onChangeCB={formatWithMask}
                  inputMode="numeric"
                  className="input-card fs-mask"
                  autoComplete="cc-number"
                  data-testid="input-card"
                />
              </FormComponents.FormColumn>
              <FormComponents.FormColumn md="6">
                <InputControl
                  label="Nome do titular"
                  name="card_client_name"
                  placeholder="Como está gravado no cartão"
                  errors={errors}
                  register={register}
                  className="input-card-client-name fs-mask"
                  autoComplete="cc-name"
                  data-testid="input-card_client_name"
                />
              </FormComponents.FormColumn>
            </FormComponents.FormRow>

            <FormComponents.FormRow>
              <FormComponents.FormColumn md="3">
                <InputControl
                  label="Validade do cartão"
                  name="card_valid_date"
                  placeholder="MM/AA"
                  errors={errors}
                  register={register}
                  mask="99/99"
                  inputMode="numeric"
                  className="input-card-valid-date fs-mask"
                  autoComplete="cc-exp"
                  data-testid="input-card_valid_date"
                />
              </FormComponents.FormColumn>
              <FormComponents.FormColumn md="3">
                <FormComponents.FormLabel>
                  CVV
                  <TooltipInfo
                    label={`Código de verificação\n localizado no verso do cartão.`}
                  />
                </FormComponents.FormLabel>
                <InputControl
                  label=""
                  name="card_cvv"
                  placeholder="000"
                  errors={errors}
                  register={register}
                  mask="9999"
                  inputMode="numeric"
                  className="input-card-cvv fs-mask"
                  autoComplete="cc-csc"
                  data-testid="input-card_cvv"
                />
              </FormComponents.FormColumn>
              <FormComponents.FormColumn md="6">
                <FormComponents.FormSelection
                  $extended
                  name="installments"
                  onChange={option => {
                    clearErrors('installments');
                    handleChangeValue('installments', `${option.value}`);
                  }}
                  label="Parcelamento"
                  defaultValue={installmentsOptions.length === 1 ? installmentsOptions[0].value : userData.installments}
                  placeholder="Selecione"
                  className="input-card-installments"
                  helperText={
                    errors.installments ? errors.installments.message : ''
                  }
                  error={Boolean(errors.installments?.message)}
                  options={installmentsOptions}
                  data-testid="input-installments"
                />
              </FormComponents.FormColumn>
            </FormComponents.FormRow>
          </div>
          <Actions
            loading={false}
            nextStepName="Finalizar"
            showBackButtom
            prevStep={() => setCurrentStep('address')}
            prevStepClassname="checkout_botaovoltar2_linkblu"
            nextStepClassname="checkout_botaofinalizar_linkblu"
          />
        </form>
      </FormBox>
    </InternalPage>
  );
};

export default CreditCard;
